/**
 * Align two workflow snapshots into an ordered list of rows for side-by-side
 * display. Paired nodes (same id on both sides) occupy the same row; nodes
 * unique to one side are paired with a `null` slot on the other so vertical
 * alignment conveys add / remove / change at a glance.
 *
 * The merge walks both sequences with two pointers preserving their original
 * order. Removed nodes slot in at their current-side order; added nodes slot
 * in at their proposed-side order. Each side is ordered topologically from its
 * edges first, so a node added mid-flow lands between its neighbours rather
 * than wherever its (insertion-order) `orderIndex` happened to fall.
 */

import { topoOrderNodes } from '@chatsdet/workflow-diff';
import type { DiffEdge, DiffNode, SnapshotDiff } from './diffSnapshot';

export type RowState = 'unchanged' | 'changed' | 'added' | 'removed';
export type BranchLabel = 'true' | 'false' | null;

export interface NodeWithBranch {
  node: DiffNode;
  /** Branch this node sits on, if reached via a conditional edge. */
  branch: BranchLabel;
}

export interface AlignedRow {
  key: string;
  state: RowState;
  current: NodeWithBranch | null;
  proposed: NodeWithBranch | null;
}

/**
 * Given a snapshot's edges, label every node by which conditional branch it
 * sits on. A node's branch is set when its incoming edge is `condition_true`
 * or `condition_false`, and that label propagates forward through `default`
 * edges until another condition is hit. Used to render TRUE / FALSE chips
 * on the whole branch, not just the immediate child of the condition.
 */
export function deriveBranchMap(edges: DiffEdge[]): Map<string, BranchLabel> {
  const result = new Map<string, BranchLabel>();
  const outgoing = new Map<string, DiffEdge[]>();
  for (const e of edges) {
    const arr = outgoing.get(e.sourceNodeId) ?? [];
    arr.push(e);
    outgoing.set(e.sourceNodeId, arr);
  }

  const queue: string[] = [];
  for (const e of edges) {
    if (e.edgeType === 'condition_true') {
      result.set(e.targetNodeId, 'true');
      queue.push(e.targetNodeId);
    } else if (e.edgeType === 'condition_false') {
      result.set(e.targetNodeId, 'false');
      queue.push(e.targetNodeId);
    }
  }

  // Index-pointer BFS: `Array.prototype.shift()` is O(n), so a naïve queue
  // becomes O(n²) for long linear chains. Using an index avoids the copy.
  for (let head = 0; head < queue.length; head += 1) {
    const id = queue[head]!;
    const label = result.get(id);
    if (!label) continue;
    for (const e of outgoing.get(id) ?? []) {
      if (e.edgeType !== 'default') continue;
      if (result.has(e.targetNodeId)) continue;
      result.set(e.targetNodeId, label);
      queue.push(e.targetNodeId);
    }
  }
  return result;
}

export function alignRows(
  currentNodes: DiffNode[],
  proposedNodes: DiffNode[],
  diff: SnapshotDiff,
  currentEdges: DiffEdge[] = [],
  proposedEdges: DiffEdge[] = [],
): AlignedRow[] {
  const currentBranch = deriveBranchMap(currentEdges);
  const proposedBranch = deriveBranchMap(proposedEdges);
  const withBranch = (
    node: DiffNode,
    which: 'current' | 'proposed',
  ): NodeWithBranch => ({
    node,
    branch:
      (which === 'current' ? currentBranch : proposedBranch).get(node.id) ?? null,
  });

  const changedIds = new Set(diff.changedNodes.map((c) => c.id));
  const proposedIds = new Set(proposedNodes.map((n) => n.id));
  const currentById = new Map(currentNodes.map((n) => [n.id, n]));

  // Order each side topologically from its own edges (falling back to
  // orderIndex only when a side has no edges). orderIndex reflects node
  // creation order, so a node inserted mid-flow would otherwise sort to the
  // bottom — e.g. an action added between Login and End rendering *after* End.
  const cur = topoOrderNodes(currentNodes, currentEdges);
  const prop = topoOrderNodes(proposedNodes, proposedEdges);

  const rows: AlignedRow[] = [];
  let i = 0;
  let j = 0;

  while (i < cur.length || j < prop.length) {
    const c = cur[i];
    const p = prop[j];

    if (c && p && c.id === p.id) {
      rows.push({
        key: c.id,
        state: changedIds.has(c.id) ? 'changed' : 'unchanged',
        current: withBranch(c, 'current'),
        proposed: withBranch(p, 'proposed'),
      });
      i += 1;
      j += 1;
      continue;
    }

    if (c && !proposedIds.has(c.id)) {
      rows.push({
        key: `removed-${c.id}`,
        state: 'removed',
        current: withBranch(c, 'current'),
        proposed: null,
      });
      i += 1;
      continue;
    }

    if (p && !currentById.has(p.id)) {
      rows.push({
        key: `added-${p.id}`,
        state: 'added',
        current: null,
        proposed: withBranch(p, 'proposed'),
      });
      j += 1;
      continue;
    }

    if (p) {
      const matchInCurrent = currentById.get(p.id);
      if (matchInCurrent) {
        rows.push({
          key: p.id,
          state: changedIds.has(p.id) ? 'changed' : 'unchanged',
          current: withBranch(matchInCurrent, 'current'),
          proposed: withBranch(p, 'proposed'),
        });
      }
      j += 1;
    } else if (c) {
      i += 1;
    }
  }

  return rows;
}
